import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORE = "vb_scroll_pos";
const read = () => { try { return JSON.parse(sessionStorage.getItem(STORE) || "{}"); } catch { return {}; } };

/**
 * PUSH/REPLACE -> scroll to top. POP (browser/Android Back/Forward) -> restore the saved position
 * for that history entry, waiting for async content to grow tall enough (max ~3s, stops on user input).
 */
export default function ScrollToTop() {
  const { pathname, search, key } = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef(key);
  const lastKey = useRef(key);
  // Switch the save-key before the DOM swaps so a height-clamp scroll is not recorded for the old page.
  if (lastKey.current !== key) { lastKey.current = key; keyRef.current = key; }

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const all = read();
        all[keyRef.current] = window.scrollY;
        const keys = Object.keys(all);
        if (keys.length > 60) delete all[keys[0]];
        try { sessionStorage.setItem(STORE, JSON.stringify(all)); } catch { /* storage full */ }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    const target = navType === "POP" ? read()[key] : 0;
    if (!target) { window.scrollTo(0, 0); return; }
    let stop = false;
    const cancel = () => { stop = true; };
    window.addEventListener("wheel", cancel, { passive: true, once: true });
    window.addEventListener("touchstart", cancel, { passive: true, once: true });
    const started = Date.now();
    const tick = () => {
      if (stop) return;
      const reachable = document.documentElement.scrollHeight - window.innerHeight >= target - 2;
      window.scrollTo(0, target);
      if (!reachable && Date.now() - started < 3000) setTimeout(tick, 100);
    };
    tick();
    return () => { stop = true; window.removeEventListener("wheel", cancel); window.removeEventListener("touchstart", cancel); };
  }, [pathname, search, key, navType]);

  return null;
}
